import type { FC } from 'react'

import type { Filter as AdditionalInfoFilterT } from '@/components/Filter/AdditionalInfoFilter'
import AdditionalInfoFilter, {
  DEFAULT_FILTER as DEFAULT_ADDITIONAL_INFO_FILTER,
} from '@/components/Filter/AdditionalInfoFilter'
import type { Filter as AgeOfReferenceFilterT } from '@/components/Filter/AgeOfReferenceFilter'
import AgeOfReferenceFilter, {
  DEFAULT_FILTER as DEFAULT_AGE_OF_REFERENCE_FILTER,
} from '@/components/Filter/AgeOfReferenceFilter'
import type { Filter as ApplicantTypeT } from '@/components/Filter/ApplicantTypeFilter'
import ApplicantTypeFilter, {
  DEFAULT_FILTER as DEFAULT_APPLICANT_TYPE_FILTER,
} from '@/components/Filter/ApplicantTypeFilter'
import type { Filter as FraudFilterT } from '@/components/Filter/FraudFilter'
import FraudFilter, {
  DEFAULT_FILTER as DEFAULT_FRAUD_FILTER,
} from '@/components/Filter/FraudFilter'
import type { Filter as IdentityNeedsActionSectionFilterT } from '@/components/Filter/IdentitySectionFilter'
import IdentityNeedsActionSectionFilter, {
  DEFAULT_FILTER as DEFAULT_IDENTITY_SECTION_FILTER,
} from '@/components/Filter/IdentitySectionFilter'
import type { Filter as IncomeNeedsActionSectionFilterT } from '@/components/Filter/IncomeSectionFilter'
import IncomeNeedsActionSectionFilter, {
  DEFAULT_FILTER as DEFAULT_INCOME_SECTION_FILTER,
} from '@/components/Filter/IncomeSectionFilter'
import type { Filter as NoOwnerFilterT } from '@/components/Filter/NoOwnerFilter'
import NoOwnerFilter, {
  DEFAULT_FILTER as DEFAULT_NO_OWNER_FILTER,
} from '@/components/Filter/NoOwnerFilter'
import type { Filter as ProductFilterT } from '@/components/Filter/ProductFilter'
import ProductFilter, {
  DEFAULT_FILTER as DEFAULT_PRODUCT_FILTER,
} from '@/components/Filter/ProductFilter'
import type { Filter as QueueSettingsFilterT } from '@/components/Filter/QueueSettingsFilter'
import { DEFAULT_FILTER as QUEUE_SETTINGS_FILTER } from '@/components/Filter/QueueSettingsFilter'
import QueueSettingsFilter from '@/components/Filter/QueueSettingsFilter'
import type { Filter as CompanyGroupFilterT } from '@/components/Filter/ReferenceGroupFilter'
import CompanyGroupFilter, {
  DEFAULT_FILTER as DEFAULT_REFERENCE_GROUP_FILTER,
} from '@/components/Filter/ReferenceGroupFilter'
import Section from '@/components/Filter/Section'
import SectionBreak from '@/components/Filter/SectionBreak'
import SectionFilter, {
  DEFAULT_FILTER as DEFAULT_SECTION_FILTER,
} from '@/components/Filter/SectionFilter'
import type { Filter as SectionFilterT } from '@/components/Filter/SectionFilter'
import type { Filter as SLAFilterT } from '@/components/Filter/SLAFilter'
import SLAFilter, {
  DEFAULT_FILTER as DEFAULT_SLA_FILTER,
} from '@/components/Filter/SLAFilter'
import type { Filter as SwimLaneGroupFilterT } from '@/components/Filter/SwimLaneGroupFilter'
import SwimLaneGroupFilter, {
  DEFAULT_FILTER as DEFAULT_SWIM_LANE_GROUP_FILTER,
} from '@/components/Filter/SwimLaneGroupFilter'
import type { Filter as TenancyStartDateFilterT } from '@/components/Filter/TenancyStartDateFilter'
import TenancyStartDateFilter, {
  DEFAULT_FILTER as DEFAULT_TENANCY_START_DATE_FILTER,
} from '@/components/Filter/TenancyStartDateFilter'
import ScrollContainer from '@/components/ScollContainer'

export interface FilterT {
  queueSettings: QueueSettingsFilterT
  identity: IdentityNeedsActionSectionFilterT
  credit: SectionFilterT
  income: IncomeNeedsActionSectionFilterT
  residential: SectionFilterT
  applicantType: ApplicantTypeT
  product: ProductFilterT
  noOwner: NoOwnerFilterT
  referenceGroup: CompanyGroupFilterT
  swimLaneGroup: SwimLaneGroupFilterT
  fraud: FraudFilterT
  tenancyStartDate: TenancyStartDateFilterT
  sla: SLAFilterT
  additionalInfo: AdditionalInfoFilterT
  ageOfReference: AgeOfReferenceFilterT
}

export const DEFAULT_FILTER: FilterT = {
  queueSettings: QUEUE_SETTINGS_FILTER,
  identity: DEFAULT_IDENTITY_SECTION_FILTER,
  credit: DEFAULT_SECTION_FILTER,
  income: DEFAULT_INCOME_SECTION_FILTER,
  residential: DEFAULT_SECTION_FILTER,
  applicantType: DEFAULT_APPLICANT_TYPE_FILTER,
  product: DEFAULT_PRODUCT_FILTER,
  noOwner: DEFAULT_NO_OWNER_FILTER,
  referenceGroup: DEFAULT_REFERENCE_GROUP_FILTER,
  swimLaneGroup: DEFAULT_SWIM_LANE_GROUP_FILTER,
  fraud: DEFAULT_FRAUD_FILTER,
  tenancyStartDate: DEFAULT_TENANCY_START_DATE_FILTER,
  sla: DEFAULT_SLA_FILTER,
  additionalInfo: DEFAULT_ADDITIONAL_INFO_FILTER,
  ageOfReference: DEFAULT_AGE_OF_REFERENCE_FILTER,
}

export interface Props {
  filter: FilterT
  onChangeFilter: (filter: FilterT) => void
}

const OutcomeReviewFilter: FC<Props> = ({ filter, onChangeFilter }) => (
  <ScrollContainer>
    <Section>
      <QueueSettingsFilter
        filter={filter.queueSettings}
        onChangeFilter={queueSettingsFilter =>
          onChangeFilter({
            ...filter,
            queueSettings: queueSettingsFilter,
          })
        }
        priorityProductSelected={filter.product.priority}
      />
    </Section>
    <SectionBreak />
    <Section title="Identification">
      <IdentityNeedsActionSectionFilter
        filter={filter.identity}
        onChangeFilter={newFilter =>
          onChangeFilter({
            ...filter,
            identity: newFilter,
          })
        }
      />
    </Section>
    <SectionBreak />
    <Section title="Credit">
      <SectionFilter
        filter={filter.credit}
        onChangeFilter={newFilter =>
          onChangeFilter({
            ...filter,
            credit: newFilter,
          })
        }
      />
    </Section>
    <SectionBreak />
    <Section title="Income">
      <IncomeNeedsActionSectionFilter
        filter={filter.income}
        onChangeFilter={newFilter =>
          onChangeFilter({
            ...filter,
            income: newFilter,
          })
        }
      />
    </Section>
    <SectionBreak />
    <Section title="Residential">
      <SectionFilter
        filter={filter.residential}
        onChangeFilter={newFilter =>
          onChangeFilter({
            ...filter,
            residential: newFilter,
          })
        }
      />
    </Section>
    <SectionBreak />
    <Section title="Applicant Type">
      <ApplicantTypeFilter
        filter={filter.applicantType}
        onChangeFilter={applicantType =>
          onChangeFilter({
            ...filter,
            applicantType: applicantType,
          })
        }
      />
    </Section>
    <SectionBreak />
    <Section title="Product">
      <ProductFilter
        filter={filter.product}
        onChangeFilter={productFilter =>
          onChangeFilter({
            ...filter,
            product: productFilter,
          })
        }
        onlyShowWorkingQueue={filter.queueSettings.onlyShowWorkingQueue}
      />
    </Section>
    <SectionBreak />
    <Section title="Group">
      <CompanyGroupFilter
        filter={filter.referenceGroup}
        onChangeFilter={referenceGroupFilter =>
          onChangeFilter({
            ...filter,
            referenceGroup: referenceGroupFilter,
          })
        }
      />
    </Section>
    <SectionBreak />
    <Section title="Fraud">
      <FraudFilter
        filter={filter.fraud}
        onChangeFilter={newFraudFilter =>
          onChangeFilter({
            ...filter,
            fraud: newFraudFilter,
          })
        }
      />
    </Section>
    <SectionBreak />
    <Section title="Squad">
      <SwimLaneGroupFilter
        filter={filter.swimLaneGroup}
        onChangeFilter={newFilter =>
          onChangeFilter({ ...filter, swimLaneGroup: newFilter })
        }
      />
    </Section>
    <SectionBreak />
    <Section title="Tenancy move in date">
      <TenancyStartDateFilter
        filter={filter.tenancyStartDate}
        onChangeFilter={newFilter =>
          onChangeFilter({ ...filter, tenancyStartDate: newFilter })
        }
      />
    </Section>
    <SectionBreak />
    <Section title="SLA">
      <SLAFilter
        filter={filter.sla}
        onChangeFilter={newFilter => onChangeFilter({ ...filter, sla: newFilter })}
      />
    </Section>
    <SectionBreak />
    <Section title="Additional info">
      <AdditionalInfoFilter
        filter={filter.additionalInfo}
        onChangeFilter={newFilter =>
          onChangeFilter({ ...filter, additionalInfo: newFilter })
        }
      />
    </Section>
    <SectionBreak />
    <Section title="Age of reference">
      <AgeOfReferenceFilter
        filter={filter.ageOfReference}
        onChangeFilter={newFilter =>
          onChangeFilter({ ...filter, ageOfReference: newFilter })
        }
      />
    </Section>
    <SectionBreak />
    <Section title="Ownership">
      <NoOwnerFilter
        filter={filter.noOwner}
        onChangeFilter={newFilter => onChangeFilter({ ...filter, noOwner: newFilter })}
      />
    </Section>
  </ScrollContainer>
)

export default OutcomeReviewFilter
